/* eslint-disable prefer-arrow/prefer-arrow-functions */
import {
  Dispatch,
  FormEvent,
  ForwardedRef,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { OurValuesData } from "../../../../../../models";
import { updateDeveloperListing } from "../../../../../../service/organisation";
import { ServiceError, Status } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { areObjectsDeepEqual, useIsLoadingWrapper } from "../../../../../../utils";
import { Toast } from "../../../../../../widget";
import {
  DeveloperFormNames,
  DeveloperMarketingAssetsFormChangedAction,
  DeveloperMarketingAssetsSubmitHandler,
} from "../../models";

interface UseOurValuesFormReturnData {
  enabled: boolean;
  content: string | null;
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  setEnabled: Dispatch<SetStateAction<boolean>>;
  setContent: Dispatch<SetStateAction<string | null>>;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  handleCancel: () => void;
  hasPermission: (permission: string) => boolean;
}

export const useOurValuesForm = (
  ourValuesDefaultValues: OurValuesData,
  ourValuesOldDefaultValues: OurValuesData,
  listingRowVersion: number,
  setListingRowVersion: Dispatch<SetStateAction<number>>,
  dispatch: Dispatch<DeveloperMarketingAssetsFormChangedAction>,
  forwardedRef: ForwardedRef<DeveloperMarketingAssetsSubmitHandler>,
  getCurrentFormsData: (formWhichWasSaved: string) => string
): UseOurValuesFormReturnData => {
  const { currentDeveloperUuid, hasPermission } = useAuth();

  const [submittedValues, setSubmittedValues] = useState<OurValuesData>(ourValuesOldDefaultValues);
  const [errors, setErrors] = useState<ServiceError[] | undefined>();

  const [enabled, setEnabled] = useState(ourValuesDefaultValues.enabled);
  const [content, setContent] = useState(ourValuesDefaultValues.content);
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  useImperativeHandle(forwardedRef, () => ({
    getOurValuesData() {
      return {
        enabled,
        content,
      };
    },
    getOurValuesSubmittedData() {
      return {
        ...submittedValues,
      };
    },
    setOurValuesErrors(previewErrors: ServiceError[]) {
      setErrors(previewErrors);
    },
  }));

  const getCurrentFormData = useCallback((): OurValuesData => {
    return {
      enabled,
      content: content || null,
    };
  }, [enabled, content]);

  useEffect(() => {
    dispatch({
      type: "SET_OUR_VALUES_FORM_CHANGED",
      value: !areObjectsDeepEqual(submittedValues, getCurrentFormData()),
    });
  }, [enabled, content, submittedValues]);

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);
    if (currentDeveloperUuid) {
      const newOurValuesValues = {
        enabled,
        content,
      };

      const res = await updateDeveloperListing({
        developerUuid: currentDeveloperUuid,
        content: getCurrentFormsData(DeveloperFormNames.OurValues),
        rowVersion: listingRowVersion,
      });

      if (res.status === Status.Success && res.data) {
        Toast.success({ message: "Our values details changed successfully" });
        setSubmittedValues({
          ...newOurValuesValues,
        });
        setListingRowVersion(res.data.rowVersion);
      }

      if (res.status === Status.Error) {
        setErrors(res.errors);
      }
    }
  }, setIsHandleSubmitLoading);

  const handleCancel = (): void => {
    setEnabled(submittedValues.enabled);
    setContent(submittedValues.content);
    setErrors([]);
  };

  return {
    enabled,
    content,
    errors,
    isHandleSubmitLoading,
    setEnabled,
    setContent,
    handleSubmit,
    handleCancel,
    hasPermission,
  };
};
